// ============================================================================
// Trainer-side activity hooks (provenance-tagged exercises, Wave 1).
// ----------------------------------------------------------------------------
// The calendar feed merges into the dashboard/calendar grid alongside sessions.
// The per-client list powers the client-detail "logged activities" view. Suggest
// (homework) / update / delete all invalidate the activities root so the merged
// calendar + per-client list recompute; the live-sync layer additionally
// invalidates on any `activity` event broadcast from the client portal.
// ============================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { activitiesApi, qk } from "../api";
import type { ActivityInput } from "../api";
import type { ActivityCalendarRange } from "../api/activities";

export function useActivityCalendar(range: ActivityCalendarRange = {}) {
  return useQuery({
    queryKey: qk.activities.calendar(range.start, range.end),
    queryFn: ({ signal }) => activitiesApi.calendar(range, signal),
  });
}

export function useClientActivities(clientId: number | undefined) {
  return useQuery({
    queryKey: qk.activities.forClient(clientId ?? -1),
    queryFn: ({ signal }) =>
      activitiesApi.listForClient(clientId as number, signal),
    enabled: clientId !== undefined && clientId >= 0,
  });
}

function invalidateActivities(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.activities.root });
}

export function useSuggestActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, body }: { clientId: number; body: ActivityInput }) =>
      activitiesApi.suggest(clientId, body),
    onSuccess: () => invalidateActivities(qc),
  });
}

export function useUpdateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      activityId,
      body,
    }: {
      activityId: number;
      body: ActivityInput;
    }) => activitiesApi.update(activityId, body),
    onSuccess: () => invalidateActivities(qc),
  });
}

export function useDeleteActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (activityId: number) => activitiesApi.remove(activityId),
    onSuccess: () => invalidateActivities(qc),
  });
}
